import styled from 'styled-components';
import isNumber from 'lodash/isNumber';
import { ListAction } from 'modules/campaigns/editor/types';
import { QueryListsSizesReturnValue } from 'modules/campaigns/editor/hooks';
import { AudienceList } from 'modules/domains/campaign';
import { Card } from 'ui/card';
import { Close24 } from 'ui/icons';
import { CircleSpinner } from 'ui/spinner';
import { Tooltip } from 'ui/tooltip';
import { IconButton, BUTTON_VARIANTS } from 'ui/button';
import { spacing, colors, smallShadow, body2 } from 'ui/theme';
import { localizeInteger } from 'utils/i18nHelpers';
import { pxToRem } from 'utils/convert';
import { getListText } from './utils';

export const LIST_DETAIL_TEST_ID = 'list-detail';
export const LIST_DETAIL_TYPE_TEST_ID = `${LIST_DETAIL_TEST_ID}-type`;
export const LIST_DETAIL_TOTAL_TEST_ID = `${LIST_DETAIL_TEST_ID}-total`;

const baseListCardTestId = 'list-card';
const buildIdentifier = (id: number, listType: ListAction) => `${id}-${listType}`;
export const getListCardTestId = (id: number, listType: ListAction) =>
  `${buildIdentifier(id, listType)}-${baseListCardTestId}`;
export const getListCardNameTestId = (id: number, listType: ListAction) =>
  `${buildIdentifier(id, listType)}-${baseListCardTestId}-name`;
export const getListCardSizeTestId = (id: number, listType: ListAction) =>
  `${buildIdentifier(id, listType)}-${baseListCardTestId}-size`;
export const getListCardRemoveItemTestId = (id: number, listType: ListAction) =>
  `${buildIdentifier(id, listType)}-${baseListCardTestId}-remove-item`;
export const getListCardLoadingTestId = (id: number, listType: ListAction) =>
  `${buildIdentifier(id, listType)}-${baseListCardTestId}-loading`;

const ListCardWrapper = styled(Card)`
  display: flex;
  justify-content: space-between;
  padding: ${spacing.base};
  background: ${colors.white};
  border-left: ${spacing.single} solid ${colors.grey15};
  position: relative;
  border-radius: ${pxToRem(8)};
  ${smallShadow};
`;

const ListDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.quarter};
`;
const ListName = styled.div`
  ${body2}
  font-weight: 500;
  overflow-wrap: anywhere;
`;
const ListSize = styled.div`
  ${body2}
  color: ${colors.secondaryText};
  display: inline-flex;
  gap: ${spacing.quarter};
`;

type ListContactsSizeProps = {
  id: number;
  listType: ListAction;
  size?: number;
  isLoading: boolean;
};

const ListContactsSize = ({ id, listType, size, isLoading }: ListContactsSizeProps) => {
  const text = isNumber(size)
    ? `${localizeInteger(size as number)} ${getListText(size as number)}`
    : `List size not available`;

  return isLoading ? (
    <>
      <CircleSpinner size={body2.fontSize} data-test={getListCardLoadingTestId(id, listType)} />
      <span>Contacts</span>
    </>
  ) : (
    <span>{text}</span>
  );
};

type ListCardProps = {
  id: number;
  listType: ListAction;
  name: string;
  listSizeData: QueryListsSizesReturnValue;
  removeList: (item: AudienceList) => void;
};

export const ListCard = ({ id, name, listType, listSizeData, removeList }: ListCardProps) => {
  const { isLoading, size } = listSizeData;

  return (
    <ListCardWrapper data-test={getListCardTestId(id, listType)}>
      <ListDetails>
        <ListName data-test={getListCardNameTestId(id, listType)}>
          <Tooltip isEnabled tooltipContent={name}>
            {name}
          </Tooltip>
        </ListName>
        <ListSize data-test={getListCardSizeTestId(id, listType)}>
          <ListContactsSize id={id} listType={listType} size={size} isLoading={isLoading} />
        </ListSize>
      </ListDetails>
      <IconButton
        data-test={getListCardRemoveItemTestId(id, listType)}
        variant={BUTTON_VARIANTS.GHOST}
        onClick={() => removeList({ id, name })}
      >
        <Close24 />
      </IconButton>
    </ListCardWrapper>
  );
};
